"use client";

import clsx from "clsx";
import React, { type HTMLProps, useContext, useEffect, useId, useRef, useState } from "react";
import type { ControllerFieldState } from "react-hook-form";
import { get, set } from "react-hook-form";
import { Slot } from "src/components/common/Composition";
import { useForm, useFormState, useSubscribe } from "src/components/form/Form/FormContext";
import styles from "src/components/form/FormControl/FormControl.module.scss";
import {
  FormControlContext,
  type FormControlInfo,
} from "src/components/form/FormControl/FormControlContext";
import type { InputProps } from "src/components/form/InputTypes";
import { useDebouncedCallback, useForceUpdate } from "src/lib/hooks/ClientHooks";
import { isDeepEqual } from "src/lib/MiscFrontendUtils";
import { useExtracted, useTranslateText } from "#i18n";

export type FormControlProps = Omit<
  HTMLProps<HTMLDivElement>,
  "children" | "value" | "onChange" | "defaultValue" | "label" | "size"
> & {
  name?: string;
  defaultValue?: any;
  label?: React.ReactNode;
  value?: any;
  id?: string;
  children?: React.ReactNode | ((field: FormControlInfo) => React.ReactNode);
  onValueProcess?: (value: any) => any;
  error?: string;
  validate?: (value: any) => string | true;
};

const FormControl = (props: FormControlProps) => {
  const {
    name,
    className,
    onValueProcess,
    required,
    label,
    error,
    children,
    defaultValue,
    id,
    validate,
    value,
    disabled,
    ...otherProps
  } = props;
  const translateMessage = useExtracted();
  const translateText = useTranslateText();
  const internalId = useId();
  const currentId = id ?? internalId;
  const form = useForm();

  const formState = useFormState((state) => {
    return {
      defaultValues: state.defaultValues,
    };
  });

  const field =
    form && name
      ? form.register(name, {
          value: value,
          // Only this field's own flag. Form-level disabled reaches inputs through useFormState; letting
          // register() inherit it makes react-hook-form update Form while this component renders
          // whenever the form toggles between disabled and enabled.
          disabled: Boolean(disabled),
          required: required,
          validate: (value, values) => {
            if (validate) {
              const error = validate(value);
              if (typeof error === "string") {
                return error;
              }
            }
            if (required && !value) {
              return translateMessage("Required");
            }

            return true;
          },
        })
      : undefined;

  useEffect(() => {
    if (name) {
      form?.trigger(name);
    }
  }, [error]);

  const currentName = name ?? currentId;
  const { formDisabled, submitCount } =
    useFormState((state) => ({
      formDisabled: state.disabled,
      submitCount: state.submitCount,
    })) || {};

  const resolvedDisabled = disabled || field?.disabled || formDisabled;
  const [stateError, setStateError] = useState<string | undefined>(undefined);

  const [displayInvalid, _setDisplayInvalid] = useState(() => {
    if (form && name) {
      const currentValue = get(form.getValues(), name);
      const defaultValue = get(form.control._defaultValues, name);
      if (currentValue !== defaultValue) {
        return true;
      }
    }
    return false;
  });
  const setDisplayInvalid = useDebouncedCallback(
    (displayInvalid: boolean) => {
      _setDisplayInvalid(displayInvalid);
    },
    [],
    600,
  );

  // Schema messages marked with `msg()` translate; other text passes through.
  const fieldError = translateText(
    useFormControlState(currentName, (state) => state?.error?.message),
  );
  const resolvedError = error || stateError || fieldError;
  const invalid = Boolean(resolvedError);

  /*useUpdateEffect(() => {
    setDisplayInvalid.immediate(true);
  }, [submitCount]);*/

  const fallbackRefObject = useRef<any>({});

  const resolvedValue = value ?? (name ? get(form?.control._formValues, name) : undefined);

  const fieldInfo: FormControlInfo = {
    getState: () => form?.getFieldState(currentName) as any,
    id: currentId,
    name: currentName,
    disabled: resolvedDisabled,
    error: resolvedError,
    invalid: invalid,
    displayInvalid: invalid && Boolean(error || displayInvalid || submitCount),
    label,
    required,
    inputProps: {
      error,
      id: currentId,

      ...field,
      onBlur: async (e) => {
        if (field?.onBlur) {
          await field.onBlur(e);
        }
        setDisplayInvalid.flush();
      },
      onChange: async (e) => {
        fallbackRefObject.current.value = e.target.value;
        fallbackRefObject.current.checked = e.target.checked;
        setDisplayInvalid.immediate(false);
        if (field?.onChange) {
          await field.onChange(e);
          setDisplayInvalid(true);
        } else {
          const value = e.target.value;
          if (validate) {
            const error = validate(value);
            setStateError(typeof error === "string" ? error : undefined);
          }
          setDisplayInvalid(true);
        }
      },
      disabled: resolvedDisabled,
      // An input is either controlled or uncontrolled: only fall back to defaultValue without a value.
      ...(resolvedValue === undefined
        ? {
            defaultValue:
              (formState?.defaultValues && name ? get(formState.defaultValues, name) : undefined) ??
              defaultValue,
          }
        : { value: resolvedValue }),
      name: currentName,
      ref: (ref) => {
        if (field) {
          const oldValues = form ? { ...form.control._formValues } : undefined;

          if (ref && ref.tagName !== "INPUT" && ref.tagName !== "TEXTAREA") {
            fallbackRefObject.current.name = currentName;
            fallbackRefObject.current.focus = () => {
              const target = ref.matches("button, [tabindex]")
                ? ref
                : ref.querySelector("input:not(:disabled), button:not(:disabled), [tabindex='0']");
              target?.focus();
            };
            field.ref(fallbackRefObject.current);
          } else {
            if (ref && !ref.type) {
              // prevent react-hook-form from firing an update when there is no type
              ref.type = "control";
            }
            field.ref(ref);
          }
          // we need to work around an issue where react-hook-form looks up a default value from the ref
          if (oldValues && form) {
            for (const key in form.control._formValues) {
              set(form.control._formValues, key, get(oldValues, key));
            }
          }
        }
      },
      required,
      onValueProcess,
    },
  };

  const [forceUpdate] = useForceUpdate();

  useEffect(() => {
    if (form && field) {
      const subscription = form.watch((watched, info) => {
        if (!info.name || info.name === field.name) {
          forceUpdate();
        }
      });
      return () => {
        subscription?.unsubscribe();
      };
    }
  }, [form, field]);

  return (
    <div
      className={clsx("flex flex-col relative gap-form-control", className)}
      data-name={currentName}
      data-id={currentId}
      //data-disabled={resolvedDisabled || undefined}
      {...otherProps}
    >
      <FormControlContext.Provider value={fieldInfo}>
        {typeof children === "function" ? children(fieldInfo) : children}
      </FormControlContext.Provider>
    </div>
  );
};

export interface FormControlInputProps {
  children?: React.ReactElement<InputProps & React.AriaAttributes>;
}

const FormControlInput = (props: FormControlInputProps) => {
  const { children, ...otherProps } = props;
  const field = useContext(FormControlContext);
  const describedBy =
    [children?.props["aria-describedby"], field ? `${field.id}-message` : undefined]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <Slot
      {...field?.inputProps}
      {...otherProps}
      aria-describedby={describedBy}
      aria-invalid={field?.displayInvalid || undefined}
      data-invalid={field?.displayInvalid ? "true" : undefined}
    >
      {children ? React.cloneElement(children, { "aria-describedby": describedBy }) : children}
    </Slot>
  );
};

const useFormControlState = <R,>(
  name: string | undefined,
  selector?: (state: ControllerFieldState) => R,
) => {
  const form = useForm();
  const selectState = (): R => {
    if (!form || !name || !selector) return undefined as any;
    return selector(form.getFieldState(name) as any);
  };
  const [state, setState] = useState<R>(() => selectState());

  const stateRef = useRef(state);
  stateRef.current = state;

  const maybeUpdate = () => {
    const newState = selectState();
    if (isDeepEqual(newState, stateRef.current)) {
      return;
    }
    setState(newState);
  };

  useSubscribe({
    next: (stateUpdate) => {
      maybeUpdate();
    },
    // @ts-expect-error
    subject: form?.control._subjects.state,
  });
  return state;
};

export type FormControlMessageProps = React.HTMLAttributes<HTMLDivElement>;

const FormControlMessage = (props: FormControlMessageProps) => {
  const { children, className, id, ...otherProps } = props;
  const field = useContext(FormControlContext);

  const error = field?.displayInvalid ? field?.error : undefined;
  const content = error ?? children;

  return content ? (
    <div
      id={id ?? (field ? `${field.id}-message` : undefined)}
      className={clsx(styles.message, error && styles.error, className)}
      role={error ? "alert" : undefined}
      data-invalid={field?.displayInvalid ? true : undefined}
      {...otherProps}
    >
      {content}
    </div>
  ) : null;
};

export {
  type FormControlInfo,
  useFormControl,
} from "src/components/form/FormControl/FormControlContext";

FormControl.Input = FormControlInput;
FormControl.Message = FormControlMessage;

export default FormControl;
